import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { useOrgFilter } from "./OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { getAccessToken } from "@/lib/authSession";

const SocialProfileContext = createContext(null);

export function SocialProfileProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { selectedOrgId, workspaceReady } = useOrgFilter();
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const [complete, setComplete] = useState(false);
  const [profile, setProfile] = useState(null);
  const checkedRef = useRef(false);

  const isSocial = selectedOrgId === "social";

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !workspaceReady) {
      return;
    }
    if (!isSocial) {
      setLoading(false);
      setChecked(false);
      checkedRef.current = false;
      return;
    }
    const alreadyChecked = checkedRef.current;
    if (!alreadyChecked) {
      setLoading(true);
    }
    try {
      const res = await axios.get(`${API_BASE_URL}/social/me/profile`, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      setComplete(Boolean(res.data?.complete));
      setProfile(res.data?.profile || null);
    } catch (err) {
      console.error("Failed to load social profile:", err);
      setComplete(false);
      setProfile(null);
    } finally {
      setLoading(false);
      setChecked(true);
      checkedRef.current = true;
    }
  }, [isAuthenticated, workspaceReady, isSocial]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      loading,
      checked,
      complete,
      profile,
      refresh,
      setComplete,
      setProfile,
    }),
    [loading, checked, complete, profile, refresh]
  );

  return (
    <SocialProfileContext.Provider value={value}>
      {children}
    </SocialProfileContext.Provider>
  );
}

export function useSocialProfile() {
  const ctx = useContext(SocialProfileContext);
  if (!ctx) {
    throw new Error("useSocialProfile must be used within SocialProfileProvider");
  }
  return ctx;
}

/** Safe for surfaces that may render outside SocialProfileProvider. */
export function useSocialProfileOptional() {
  return useContext(SocialProfileContext);
}
